import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errorMessages";
import { Package, Wallet, BarChart3 } from "lucide-react";

// ⚠️ EMPLACEMENT IMAGE LOGIN — pour changer l'image hero, remplacer le fichier
//    src/assets/login-hero.jpg par une nouvelle image (format .jpg ou .webp,
//    orientation portrait recommandée, largeur min. 1200px, ratio ~3:4 ou 1:1).
//    Aucune autre modification de code n'est nécessaire.
import heroImage from "@/assets/login-hero.jpg";
const HERO_IMAGE_URL = heroImage;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, role } = useAuth();

  useEffect(() => {
    if (searchParams.get("expired") === "1") {
      toast.warning("Votre session a expiré. Veuillez vous reconnecter.");
      searchParams.delete("expired");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation champs manquants côté client (uniformisée)
    if (!email.trim() || !password) {
      setError("Veuillez renseigner votre e-mail et votre mot de passe.");
      return;
    }

    setLoading(true);

    try {
      // Tentative de login (avec 1 retry rapide en cas d'erreur réseau)
      let signInError: { message: string } | null = null;
      for (let attempt = 1; attempt <= 2; attempt++) {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        signInError = err ?? null;
        if (!err) break;
        const msg = err.message?.toLowerCase() || "";
        const transient = msg.includes("network") || msg.includes("fetch") || msg.includes("timeout");
        if (!transient || attempt === 2) break;
        await new Promise((r) => setTimeout(r, 600));
      }

      if (signInError) {
        setError(getErrorMessage(signInError, "auth"));
        setLoading(false);
        return;
      }
      // La session est posée par le SDK ; onAuthStateChange + useEffect navigueront vers /dashboard.
    } catch (e: any) {
      console.error("[Login] unexpected error:", e);
      setError(getErrorMessage(e, "auth"));
      setLoading(false);
    }
  };

  const goToDashboard = () => {
    window.location.href = "/dashboard";
  };


  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* ============ LEFT PANEL ============ */}
      <div className="flex flex-col px-6 sm:px-12 lg:px-16 py-8 lg:py-12 bg-background">
        {/* Mobile compact image banner */}
        <div
          className="lg:hidden -mx-6 sm:-mx-12 mb-8 h-32 bg-cover bg-center relative"
          style={{ backgroundImage: `url(${HERO_IMAGE_URL})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/85 to-foreground/40" />
          <div className="absolute bottom-3 left-6 sm:left-12">
            <p className="text-[10px] tracking-[0.2em] text-gold uppercase font-semibold [text-shadow:_0_1px_4px_rgb(0_0_0_/_60%)]">Mandjaro Ops</p>
          </div>
        </div>

        {/* Logo top */}
        <div className="hidden lg:block">
          <div className="inline-flex items-baseline gap-2">
            <span className="font-display text-2xl text-foreground">Mandjaro</span>
            <span className="text-[10px] tracking-[0.2em] text-gold uppercase font-medium">
              Ops
            </span>
          </div>
        </div>

        {/* Centered form */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[440px]">
            <div className="mb-8">
              <h1 className="font-display text-[32px] leading-tight text-foreground">
                Bon retour <span aria-hidden>👋</span>
              </h1>
              <p className="text-sm text-ink-4 mt-2">
                Connectez-vous pour reprendre la gestion de votre établissement.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-medium text-ink-3">
                  Adresse e-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nom@mandjaro.ci"
                  required
                  className="h-11 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-medium text-ink-3">
                    Mot de passe
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-[11px] text-ink-4 hover:text-foreground transition-colors"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11 text-sm"
                />
              </div>

              {error && (
                <div className="text-xs text-destructive bg-status-danger-bg px-3 py-2 rounded space-y-2">
                  <p>{error}</p>
                  {error.includes("redirection") && (
                    <Button
                      type="button"
                      onClick={goToDashboard}
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                    >
                      Aller au tableau de bord
                    </Button>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-sm font-medium bg-foreground text-background hover:bg-foreground/90"
              >
                {loading ? "Connexion…" : "Se connecter"}
              </Button>
            </form>
          </div>
        </div>

        <p className="text-center text-ink-4 text-[11px] mt-8">
          © Mandjaro 2026 — Bar &amp; Restaurant, Abidjan, Côte d'Ivoire
        </p>
        <p className="text-center text-ink-4 text-[11px] mt-2">
          <Link to="/confidentialite" className="hover:text-foreground transition-colors">
            Confidentialité
          </Link>
          {" · "}
          <Link to="/cgu" className="hover:text-foreground transition-colors">
            CGU
          </Link>
        </p>
      </div>

      {/* ============ RIGHT PANEL (desktop) ============ */}
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE_URL})` }}
      >
        {/* Overlay principal — assombrit le coin haut-droit pour l'eyebrow */}
        <div className="absolute inset-0 bg-gradient-to-tr from-foreground/95 via-foreground/65 to-foreground/30" />
        {/* Overlay bas — ancre le texte tagline */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-foreground/95 via-foreground/60 to-transparent" />

        {/* Top eyebrow */}
        <div className="absolute top-12 right-12 text-right">
          <p className="text-[10px] tracking-[0.25em] text-gold uppercase font-semibold [text-shadow:_0_1px_6px_rgb(0_0_0_/_70%)]">
            Excellence opérationnelle
          </p>
        </div>

        {/* Bottom-left tagline + features */}
        <div className="absolute bottom-12 left-12 right-12 text-background space-y-8">
          <div>
            <h2 className="font-display text-[28px] xl:text-[32px] leading-tight max-w-md text-background [text-shadow:_0_2px_12px_rgb(0_0_0_/_70%)]">
              Pilotez votre établissement avec précision.
            </h2>
            <p className="text-sm text-background/90 mt-3 max-w-md [text-shadow:_0_1px_6px_rgb(0_0_0_/_60%)]">
              Le pilotage opérationnel d'un Bar & Restaurant premium à Abidjan.
            </p>
          </div>

          <div className="flex items-center gap-6 pt-6 border-t border-background/30">
            <Feature icon={<Package size={16} />} label="Stock temps réel" />
            <Feature icon={<Wallet size={16} />} label="Caisse intégrée" />
            <Feature icon={<BarChart3 size={16} />} label="Analytics PDG" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 text-background/95 [text-shadow:_0_1px_4px_rgb(0_0_0_/_60%)]">
      <span className="text-gold">{icon}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}
